import { Timestamp, timestampDate } from "@bufbuild/protobuf/wkt";
import { ArrowRightIcon, CalendarIcon, CheckCircleIcon, FileTextIcon, TrendingUpIcon, XCircleIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useDecisions } from "@/hooks/useDecisions";
import { cn } from "@/lib/utils";
import type { Decision } from "@/types/proto/api/v1/memo_service_pb";
import { useLocale } from "@/utils/i18n";

interface DecisionsListProps {
  contractUid: string;
  ticker?: string;
  className?: string;
  limit?: number;
}

const DECISION_TYPE_LABELS: Record<string, string> = {
  BUY: "买入",
  SELL: "卖出",
  HOLD: "持有",
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  FOLLOW: "遵守契约",
  OVERRIDE: "违背契约",
  NONE: "独立决策",
};

const formatTimestamp = (timestamp: Date | { seconds: number; nanos: number } | string | Timestamp | undefined, locale: string): string => {
  if (!timestamp) {
    return "未知";
  }

  let date: Date;
  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === "string") {
    date = new Date(timestamp);
  } else if (typeof timestamp === "object" && "seconds" in timestamp) {
    date = new Date(Number(timestamp.seconds) * 1000);
  } else {
    return "未知";
  }

  return date.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPrice = (price: number): string => {
  return price.toFixed(2);
};

/**
 * DecisionsList - Displays decisions related to a Self-Contract
 *
 * Shows a list of decisions sorted by executedAt (newest first).
 * Includes badges for decision type and action type.
 */
export const DecisionsList: React.FC<DecisionsListProps> = ({ contractUid, ticker, className, limit = 10 }) => {
  const locale = useLocale();

  // Fetch decisions for this contract
  const {
    data: decisionsData,
    isLoading,
    isError,
  } = useDecisions({
    contractUid,
    ticker,
    pageSize: limit,
  });

  const decisions = decisionsData?.decisions || [];

  // Sort by executedAt (newest first)
  const sortedDecisions = [...decisions].sort((a, b) => {
    let aTime = 0;
    let bTime = 0;
    if (a.executedAt) {
      aTime = (timestampDate(a.executedAt) ?? new Date()).getTime();
    }
    if (b.executedAt) {
      bTime = (timestampDate(b.executedAt) ?? new Date()).getTime();
    }
    return bTime - aTime;
  });

  // Calculate summary stats
  const followCount = decisions.filter((d) => d.actionType === "FOLLOW").length;
  const overrideCount = decisions.filter((d) => d.actionType === "OVERRIDE").length;
  const totalDecisions = decisions.length;
  const complianceRate = totalDecisions > 0 ? followCount / totalDecisions : 0;

  if (isLoading) {
    return <div className={cn("flex items-center justify-center py-4 text-sm text-muted-foreground", className)}>加载中...</div>;
  }

  if (isError) {
    return <div className={cn("flex items-center justify-center py-4 text-sm text-destructive", className)}>加载失败</div>;
  }

  if (decisions.length === 0) {
    return (
      <div className={cn("py-4 text-center text-sm text-muted-foreground", className)}>
        <FileTextIcon className="w-6 h-6 mx-auto mb-2 opacity-50" />
        <p>暂无关联决策</p>
        <p className="text-xs mt-1">创建决策时会自动关联合约</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {/* Summary Header */}
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-muted-foreground">遵守:</span>
            <span className="font-semibold text-green-600 dark:text-green-400">{followCount}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-muted-foreground">违背:</span>
            <span className="font-semibold text-red-600 dark:text-red-400">{overrideCount}</span>
          </div>
          <div className="w-px h-4 bg-border" />
          <div className="text-sm">
            <span className="text-muted-foreground">遵守率:</span>
            <span
              className={cn(
                "ml-1 font-semibold",
                complianceRate >= 0.7 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400",
              )}
            >
              {(complianceRate * 100).toFixed(0)}%
            </span>
          </div>
        </div>
        <Badge variant="secondary" className="text-xs">
          共 {totalDecisions} 项决策
        </Badge>
      </div>

      {/* Decision Items */}
      <div className="space-y-2">
        {sortedDecisions.map((decision) => (
          <DecisionItem key={decision.name} decision={decision} locale={locale} />
        ))}
      </div>
    </div>
  );
};

interface DecisionItemProps {
  decision: Decision;
  locale: string;
}

const DecisionItem: React.FC<DecisionItemProps> = ({ decision, locale }) => {
  const isFollow = decision.actionType === "FOLLOW";
  const isOverride = decision.actionType === "OVERRIDE";

  return (
    <div className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
      {/* Header: Type + Action + Date */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Decision Type */}
          <Badge variant="outline" className="font-medium">
            {DECISION_TYPE_LABELS[decision.decision] || decision.decision}
          </Badge>

          {/* Action Type */}
          {isFollow && (
            <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-300 dark:border-green-700">
              <CheckCircleIcon className="w-3 h-3 mr-1" />
              {ACTION_TYPE_LABELS[decision.actionType]}
            </Badge>
          )}
          {isOverride && (
            <Badge className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 border-red-300 dark:border-red-700">
              <XCircleIcon className="w-3 h-3 mr-1" />
              {ACTION_TYPE_LABELS[decision.actionType]}
            </Badge>
          )}
        </div>

        {/* Date */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <CalendarIcon className="w-3 h-3" />
          {formatTimestamp(decision.executedAt, locale)}
        </div>
      </div>

      {/* Details: Price + Quantity */}
      <div className="flex items-center gap-3 text-sm mb-2">
        <div className="flex items-center gap-1">
          <TrendingUpIcon className="w-3 h-3 text-muted-foreground" />
          <span className="font-medium">{formatPrice(Number(decision.price))}</span>
        </div>
        <ArrowRightIcon className="w-3 h-3 text-muted-foreground" />
        <span className="text-muted-foreground">x {Number(decision.quantity).toFixed(0)} 股</span>
      </div>

      {/* Reason */}
      {decision.reason && <div className="text-sm text-muted-foreground line-clamp-2">{decision.reason}</div>}
    </div>
  );
};

export default DecisionsList;
